/**
 * `<ChatDrawer>` — Mantine slide-out drawer that hosts the in-call chat
 * between provider and patient. Opens from the right edge of the viewport
 * when the doctor clicks the Chat button in `<ZoomPanel>`'s controls row.
 *
 * Data flow
 *  - Messages live in `<CallSessionProvider>` via the
 *    `client.on('chat-on-message', ...)` listener wired in `joinSession`.
 *  - The drawer reads `chatMessages` from `useCallSession()` and renders
 *    each as a bubble.
 *  - Sending a message calls `useCallSession().sendChatMessage(text)`,
 *    which forwards to Zoom's `chatClient.sendToAll(text)` and pushes the
 *    sent message into local state so the doctor sees their own bubble
 *    immediately (no server round-trip).
 *
 * Layout
 *  - Header: title + close button.
 *  - Body: scrollable message list, oldest top → newest bottom; auto-
 *    scrolls to the latest bubble on new arrivals.
 *  - Footer: a textarea + Send button. Enter sends, Shift+Enter inserts a
 *    newline.
 *
 * Empty state mirrors the Transcript stream's: a small italicized note
 * inviting the doctor to start the conversation.
 */
import { useEffect, useRef, useState } from 'react'
import { ActionIcon, Box, Drawer, Group, ScrollArea, Text, Textarea } from '@mantine/core'
import { IconMessageCircle, IconSend, IconX } from '@tabler/icons-react'
import { useCallSession } from '../session'
import { logError } from '@/utils/errorUtils'

const CORAL = '#e8624a'

function formatTime(ms: number): string {
  if (!ms) return ''
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export interface ChatDrawerProps {
  opened: boolean
  onClose: () => void
}

export function ChatDrawer({ opened, onClose }: ChatDrawerProps) {
  const { chatMessages, sendChatMessage } = useCallSession()
  const [draft, setDraft] = useState('')
  const [isSending, setIsSending] = useState(false)
  const viewportRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to the latest message whenever a new one arrives. We pin
  // by message COUNT (not by ref content) because Mantine's ScrollArea
  // viewport stays mounted across opens — a smooth scroll on every new
  // message works cleanly with no special logic for the empty state.
  useEffect(() => {
    if (!opened) return
    const el = viewportRef.current
    if (!el) return
    // Use rAF so the latest bubble has rendered before scrolling.
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    })
  }, [opened, chatMessages.length])

  const handleSend = async () => {
    const trimmed = draft.trim()
    if (!trimmed || isSending) return
    setIsSending(true)
    try {
      await sendChatMessage(trimmed)
      setDraft('')
    } catch (err) {
      logError('zoom-chat', 'send message failed', err)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={360}
      withCloseButton={false}
      lockScroll={false}
      overlayProps={{ backgroundOpacity: 0.35 }}
      data-testid="zoom-chat-drawer"
      styles={{
        // Inline styles: Mantine's own white content background beats
        // Tailwind utility classes here.
        content: { background: '#0f172a', color: '#f1f5f9' },
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
      }}
    >
      {/* Header — icon chip + title/subtitle, mirroring the ZoomPanel's
          visual language (coral accent on slate). */}
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className="shrink-0 border-b border-white/[0.07] px-[14px] py-[10px]"
      >
        <Group gap={10} align="center" wrap="nowrap">
          <div
            className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full"
            style={{ background: `${CORAL}1a`, border: `1px solid ${CORAL}25` }}
          >
            <IconMessageCircle size={15} color={CORAL} strokeWidth={1.8} />
          </div>
          <div>
            <Text fw={700} size="sm" className="text-slate-100">
              In-Call Chat
            </Text>
            <Text size="xs" c="dimmed">
              Visible to everyone in the call
            </Text>
          </div>
        </Group>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={onClose}
          aria-label="Close chat"
          data-testid="zoom-chat-close"
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>

      {/* Message list — the empty state renders outside the ScrollArea so it
          can center in the drawer body instead of hugging the top. */}
      {chatMessages.length === 0 ? (
        <Box
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[14px] px-8"
          data-testid="zoom-chat-messages"
        >
          <div
            className="flex h-[50px] w-[50px] items-center justify-center rounded-full"
            style={{ background: `${CORAL}1a`, border: `1px solid ${CORAL}25` }}
          >
            <IconMessageCircle size={22} color={CORAL} strokeWidth={1.8} />
          </div>
          <div className="text-center">
            <Text size="sm" fw={600} className="mb-1 text-[#e2e8f0]">
              No messages yet
            </Text>
            <Text size="xs" c="dimmed" className="leading-[1.6]">
              Say hello — messages are visible to everyone in the call and are not saved to the
              chart.
            </Text>
          </div>
        </Box>
      ) : (
        <ScrollArea
          viewportRef={viewportRef}
          type="auto"
          scrollbarSize={6}
          className="min-h-0 flex-1"
          styles={{
            root: { background: '#0f172a' },
            viewport: { padding: '14px' },
          }}
          data-testid="zoom-chat-messages"
        >
          {chatMessages.map((m, i) => {
            const prev = chatMessages[i - 1]
            const startsGroup = !prev || prev.isOwn !== m.isOwn || prev.senderName !== m.senderName
            return (
              <Box
                key={m.id}
                data-testid={`zoom-chat-message-${m.id}`}
                className={`${i > 0 ? (startsGroup ? 'mt-[14px]' : 'mt-[3px]') : ''} ${
                  m.isOwn ? 'ml-auto' : 'mr-auto'
                } max-w-[82%]`}
              >
                {/* Sender name on every group start — own messages too:
                    multi-party calls need attribution everywhere. */}
                {startsGroup && (
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    ta={m.isOwn ? 'right' : 'left'}
                    className={m.isOwn ? 'mr-[10px] mb-[3px]' : 'mb-[3px] ml-[10px]'}
                    style={{ letterSpacing: '0.02em' }}
                  >
                    {m.senderName}
                  </Text>
                )}
                <Box
                  className={
                    m.isOwn
                      ? 'rounded-2xl rounded-br-md px-[12px] py-[7px] text-white'
                      : 'rounded-2xl rounded-bl-md bg-[#1e293b] px-[12px] py-[7px] text-slate-100'
                  }
                  style={
                    m.isOwn
                      ? { background: `linear-gradient(135deg, ${CORAL}, #d0533d)` }
                      : undefined
                  }
                >
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                    {m.message}
                  </Text>
                  <Text
                    ta="right"
                    mt={2}
                    style={{ fontSize: 10 }}
                    className={m.isOwn ? 'text-white/60' : 'text-slate-500'}
                  >
                    {formatTime(m.timestamp)}
                  </Text>
                </Box>
              </Box>
            )
          })}
        </ScrollArea>
      )}

      {/* Composer — Enter sends, Shift+Enter newline */}
      <Box className="shrink-0 border-t border-white/[0.07] p-[10px]">
        <Group gap={8} align="flex-end" wrap="nowrap">
          <Textarea
            data-autofocus
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            placeholder="Message…"
            autosize
            minRows={1}
            maxRows={4}
            radius="lg"
            style={{ flex: 1 }}
            disabled={isSending}
            onKeyDown={(e) => {
              // isComposing: Enter is committing an IME composition, not sending.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void handleSend()
              }
            }}
            styles={{
              input: {
                background: '#0b1220',
                border: '1px solid #1e293b',
                color: '#e2e8f0',
                fontSize: 13,
                lineHeight: 1.45,
              },
            }}
            data-testid="zoom-chat-input"
          />
          <ActionIcon
            variant="filled"
            size="lg"
            radius="xl"
            color={CORAL}
            onClick={() => void handleSend()}
            loading={isSending}
            disabled={!draft.trim() || isSending}
            aria-label="Send message"
            data-testid="zoom-chat-send"
          >
            <IconSend size={16} />
          </ActionIcon>
        </Group>
      </Box>
    </Drawer>
  )
}
